import { locationUtil } from '@grafana/data';
import { t } from '@grafana/i18n';
import { getAppEvents, locationService } from '@grafana/runtime';
import { getExploreUrl } from 'app/core/utils/explore';
import { getTimeSrv } from 'app/features/dashboard/services/TimeSrv';
import { type SavedQuery } from 'app/features/explore/QueryLibrary/types';
import { store } from 'app/store/store';
import { ShowConfirmModalEvent } from 'app/types/events';

export type OpenInExploreOptions = {
  /**
   * Link the Explore pane back to the saved query (editing mode). Defaults to true; set false to just
   * load the query to run it (e.g. from the command palette).
   */
  setEditSavedQueryRef?: boolean;
  /**
   * Path to return to when the user finishes editing in Explore (Discard / "Save and return"). When set,
   * it's carried on the Explore URL so QueryLibraryEditingHeader knows this edit originated from the
   * Saved Queries pages and where to navigate back to. Used by the "Edit in Explore" drawer flow.
   */
  savedQueryReturnTo?: string;
  /**
   * Skip the "Replace existing queries?" modal. Used when retrying after the dirty-form discard guard —
   * the user already confirmed replace (or there was nothing to replace) before the guard blocked.
   */
  skipReplaceConfirm?: boolean;
};

/**
 * Drawer close callback. Returns false when a close guard blocked (e.g. dirty-form discard modal);
 * true or void when the drawer closed. Callers that need to abort a follow-up navigation on block
 * should check the boolean; void return keeps back-compat with pre-boolean implementations.
 */
type CloseDrawerFn = (isSelectingQuery?: boolean, closedToEditInExplore?: boolean) => boolean | void;

export const onOpenInExplore = async (
  query: SavedQuery,
  onCloseDrawer?: CloseDrawerFn,
  options?: OpenInExploreOptions
) => {
  if (!query.query || !query.datasourceRef) {
    console.error('Missing required query or datasource reference');
    return;
  }

  // Get current explore state
  const { exploreState, currentPaneIds } = getCurrentExploreState();

  // Check if we should show the warning modal
  const currentQueries = currentPaneIds.flatMap((id) => exploreState[id]?.queries);
  if (currentQueries.length > 1 && !options?.skipReplaceConfirm) {
    // Show modal for multiple queries
    showMultipleQueriesModal(query, onCloseDrawer, options);
  } else {
    await performDirectNavigation(query, onCloseDrawer, options);
  }
};

// Helper functions

const getCurrentExploreState = () => {
  const exploreState = store.getState().explore.panes;
  const currentPaneIds = Object.keys(exploreState).filter((id) => exploreState[id] !== undefined);
  return { exploreState, currentPaneIds };
};

const performDirectNavigation = async (
  query: SavedQuery,
  onCloseDrawer?: CloseDrawerFn,
  options?: OpenInExploreOptions
) => {
  try {
    const timeRange = getTimeSrv().timeRange();
    const exploreUrl = await getExploreUrl({
      queries: [query.query],
      dsRef: query.datasourceRef,
      timeRange,
      scopedVars: undefined,
    });

    if (!exploreUrl) {
      console.error('Failed to generate explore URL');
      return;
    }

    // Close the drawer first so its dirty-form guard can intercept before we leave the current page.
    // On page-owned drawers (e.g. the Saved Queries page's EditQueryDrawer), navigating first would
    // unmount the drawer before the guard could run — the discard confirmation would never appear.
    // A false return means the guard opened the discard modal; abort the push and let the details
    // panel resume this open after the user confirms. When no close callback is passed (e.g. table
    // actions), proceed straight to Explore.
    if (onCloseDrawer?.(false, true) === false) {
      return;
    }

    // Navigate to explore. If the current page has unsaved work, Grafana's leave-page guard
    // (DashboardPrompt's history.block) intercepts this push and asks the user to save, discard,
    // or cancel before continuing. The query-library drawer is already closed by then.
    // Both extra params below are top-level and survive Explore's init URL-rewrite:
    // - savedQueryReturnTo lets the editing banner send the user back to the Saved Queries page
    //   (and reopen the drawer) after Discard / "Save and return".
    // - editSavedQueryRef puts Explore into editing mode for this saved query. Carrying it on the URL
    //   (read by Explore's init synchronizer) instead of dispatching after navigation avoids a race
    //   on a cold Explore mount where the pane isn't initialized yet / init resets the ref.
    //   Skipped when opening just to run the query (e.g. from the command palette).
    // Use URL / URLSearchParams so params land in the query string even if the base Explore URL has a
    // hash or an unusual shape — never append with string concatenation.
    const url = new URL(locationUtil.stripBaseFromUrl(exploreUrl), window.location.origin);
    if (options?.savedQueryReturnTo) {
      url.searchParams.set('savedQueryReturnTo', options.savedQueryReturnTo);
    }
    if (options?.setEditSavedQueryRef !== false && query.uid) {
      url.searchParams.set('editSavedQueryRef', query.uid);
    }
    locationService.push(`${url.pathname}${url.search}${url.hash}`);
  } catch (error) {
    console.error('Failed to open in explore:', error);
  }
};

const showMultipleQueriesModal = (
  query: SavedQuery,
  onCloseDrawer?: CloseDrawerFn,
  options?: OpenInExploreOptions
) => {
  const modalConfig = buildModalConfig(query, onCloseDrawer, options);
  getAppEvents().publish(new ShowConfirmModalEvent(modalConfig));
};

// Helper function to build modal configuration
const buildModalConfig = (
  query: SavedQuery,
  onCloseDrawer?: CloseDrawerFn,
  options?: OpenInExploreOptions
) => {
  // The command palette opens the query to run it (setEditSavedQueryRef: false), not to edit it, so
  // use "Opening" there; the "Edit in Explore" flow keeps "Editing".
  const warningText =
    options?.setEditSavedQueryRef === false
      ? t(
          'query-library.explore-modal.warning-open',
          'You have multiple queries in Explore. Opening this saved query will replace all existing queries. Do you want to continue?'
        )
      : t(
          'query-library.explore-modal.warning',
          'You have multiple queries in Explore. Editing this saved query will replace all existing queries. Do you want to continue?'
        );

  return {
    title: t('query-library.explore-modal.title', 'Replace existing queries?'),
    text: warningText,
    yesText: t('query-library.explore-modal.confirm-button', 'Replace queries'),
    noText: t('query-library.explore-modal.cancel-button', 'Cancel'),
    icon: 'exclamation-triangle' as const,
    onConfirm: () => performDirectNavigation(query, onCloseDrawer, options),
  };
};
